import { makeAutoObservable } from 'mobx';
import _ from 'lodash';
import {DomainStore} from 'shared/store';
import {endpoints, types} from 'main/tools';
import {ClockEvent} from 'main/stores/time_tracking';
import {successAlert} from 'shared/tools';
import {t} from 'shared/core';

class TimeClockState {
  store = new DomainStore();

  clockInEvent = null;
  clockOutError = null;
  errors = {};
  employee;
  note;
  dimensions = [];
  dimensionValues = [];
  collectEmployeeLocation;
  myTimesheet;
  canCreateEntries;

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  receiveProps({onClockEvent, collectEmployeeLocation, employee, myTimesheet, dimensions, canCreateEntries}) {
    this.onClockEvent = onClockEvent;
    this.collectEmployeeLocation = collectEmployeeLocation;
    this.employee = employee;
    this.myTimesheet = myTimesheet;
    this.dimensions = dimensions;
    this.canCreateEntries = canCreateEntries;
  }

  async load() {
    await this.store._compose([
      endpoints.TIME_TRACKING.CURRENT_CLOCK_IN.with(this.employee.id)
    ]);

    const clockEvent = this.store._getSingle(types.TIME_TRACKING.CLOCK_EVENT);

    if (clockEvent) {
      this.clockInEvent = new ClockEvent(clockEvent);
      this.note = clockEvent.note;
      this.dimensionValues = clockEvent.dimensionValues;
    }
  }

  updateDimensionValue(dimensionId, value) {
    const others = this.dimensionValues.filter(dv => dv.dimensionId !== dimensionId);
    this.dimensionValues = value ? [...others, value] : others;
  }

  dimensionValueFor(dimensionId) {
    return _.find(this.dimensionValues, {dimensionId});
  }

  updateNote(note) {
    this.note = note;
  }

  async clockIn() {
    const clockEvent = new ClockEvent({
      location: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      note: this.note,
      dimensionValues: this.dimensionValues
    });

    if (this.collectEmployeeLocation) {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      }).catch(() => null);

      if (!position || position.coords.accuracy >= 250) return this.openLocationErrorModal();

      clockEvent.location.latitude = position.coords.latitude;
      clockEvent.location.longitude = position.coords.longitude;
      clockEvent.location.accuracy = position.coords.accuracy;
    }

    const {model, errors} = await this.store.post(
      endpoints.TIME_TRACKING.CLOCK_IN,
      types.TIME_TRACKING.CLOCK_EVENT,
      clockEvent
    );

    this.errors = errors;

    if (model) {
      this.clockInEvent = new ClockEvent(model);
      this.clockOutError = null;
      this.onClockEvent();
    }
  }

  async clockOut() {
    const clockEvent = new ClockEvent();

    if (this.collectEmployeeLocation) {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      }).catch(() => null);

      if (!position || position.coords.accuracy >= 250) return this.openLocationErrorModal();

      clockEvent.location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };
    }

    const {model, errors} = await this.store.post(
      endpoints.TIME_TRACKING.CLOCK_OUT.with(this.employee.id),
      types.TIME_TRACKING.CLOCK_EVENT,
      clockEvent
    );

    this.errors = errors;

    if (model) {
      this.clockInEvent = null;
      this.clockOutError = model.clockOutError;
      this.onClockEvent();

      if (!this.clockOutError) {
        successAlert(t('time_tracking.time_clock.Entry saved to timesheet'));
      }
    }
  }

  dismissClockOutError() {
    this.clockOutError = null;
  }

  get isHidden() {
    return !this.clockInEvent && !this.myTimesheet;
  }

  get showAction() {
    return !this.clockInEvent || this.clockInEvent.hasLink('clockOut');
  }
}

export default TimeClockState;
